import { useEffect, useState } from "react"
import { UserTeamMemberResponse } from "../../../../../../../service/contract/team-management"
import { useLazyGetRolesQuery } from "../../../../../../../service/role-service"
import CustomerPageLayout from "../../../../../customer-page-layout/CustomerPageLayout"
import { CustomerLoader } from "../../../../../components"
import { HalfCircleCheck } from "../../../../../../../assets/icons"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import styles from "./manage-member-permissions.module.scss"
import { roleDescriptions } from "../../../team-management.constant"
import { useChangeTeamMemberRoleMutation } from "../../../../../../../service/team-management-service"
import { useSelector } from "react-redux"
import { RootState } from "../../../../../../../stores/store"
import { CustomerType } from "../../../../../../../model/customer/customer-type"

interface ManageMemberPermissionsProps {
  selectedMember: UserTeamMemberResponse | null;
  onCancel: () => void;
  onContinue: () => void;
}

type RoleDto = {
  id: string;
  name: string;
}

export const ManageMemberPermissions = ({
  selectedMember,
  onCancel,
  onContinue,
}: ManageMemberPermissionsProps) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.user);

  const [getRoles, { isLoading: isLoadingRoles, isFetching: isFetchingRoles }] = useLazyGetRolesQuery();
  const [changeRole, { isLoading: isChangingRole }] = useChangeTeamMemberRoleMutation();

  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>(selectedMember?.role?.id || "");

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const result = await getRoles({ Page: 1, PageSize: 100 }).unwrap();
        if (result) {
          setRoles(result.items.map((x) => ({ id: x.id, name: x.name })));
        }
      } catch (error: any) {
        toast.error(error?.data?.title || t("account.web.failedToFetchRoles"));
      }
    };

    fetchRoles();
  }, [getRoles]);

  useEffect(() => {
    if (selectedMember?.role?.id) {
      setSelectedRoleId(selectedMember.role.id);
    }
  }, [selectedMember]);

  const getRoleDescription = (roleName: string): string => {
    return roleDescriptions[roleName] || t("account.web.noDescriptionAvailable");
  };

  const handleApply = async () => {
    if (!selectedMember || !selectedRoleId) {
      toast.error(t("account.web.pleaseSelectARole"));
      return;
    }
    try {
      await changeRole({ userReferenceId: selectedMember.id, roleId: selectedRoleId }).unwrap();
      toast.success(t("account.web.roleUpdatedSuccessfully"));
      onContinue();
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToUpdateRole"));
    }
  };

  if (isChangingRole) {
    return (
      <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
        <CustomerLoader
          title={t("account.web.updatingRole")}
          subtitle={t("account.web.pleaseWaitUpdatingRole")}
        />
      </CustomerPageLayout>
    );
  }

  if (isLoadingRoles || isFetchingRoles) {
    return (
      <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")}>
        <CustomerLoader
          title={t("account.web.loadingRoles")}
          subtitle={t("account.web.pleaseWaitFetchingRoles")}
        />
      </CustomerPageLayout>
    );
  }

  return (
    <CustomerPageLayout title={userInfo.customerType === CustomerType.Individual ? t("common.labels.familyAccountManagement") : t("account.corporateAccountManagement")} onBack={onCancel}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{t("account.web.editRole")}</h2>
          <p className={styles.subtitle}>{t("corporate.chooseRoleFromOptions")}</p>
        </div>

        <div className={styles.rolesList}>
          {roles.map((role) => {
            const isSelected = selectedRoleId === role.id;

            return (
              <label
                key={role.id}
                className={`${styles.roleOption} ${isSelected ? styles.active : ""}`}
              >
                <input
                  type="radio"
                  name="role"
                  value={role.id}
                  checked={isSelected}
                  onChange={() => setSelectedRoleId(role.id)}
                  className={styles.radioInput}
                />
                <div className={styles.roleContent}>
                  <span className={styles.roleName}>{role.name}</span>
                  <span className={styles.roleDescription}>
                    {getRoleDescription(role.name)}
                  </span>
                </div>
                {isSelected && (
                  <span className={styles.checkmark}>
                    <HalfCircleCheck />
                  </span>
                )}
              </label>
            );
          })}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={isChangingRole}
          >
            {t("common.actions.cancel")}
          </button>
          <button
            className={styles.applyButton}
            onClick={handleApply}
            disabled={!selectedRoleId || isChangingRole || selectedRoleId === selectedMember?.role?.id}
          >
            {isChangingRole ? t("account.web.applying") : t("common.actions.apply")}
          </button>
        </div>
      </div>
    </CustomerPageLayout>
  );
}
